import fetchIntercept from 'fetch-intercept';
import jwt_decode from 'jwt-decode';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { isEqual } from 'lodash';

import {
  authChannel,
  AUTH_MESSAGE_STATUS,
  AuthBroadcastChannelKeys,
} from 'utils/broadcastAuthChannel';
import store from 'redux/store';
import { isTokenExpired } from 'utils/authUtils';
import {
  logout,
  refreshToken,
  getUserDetails,
} from 'redux/reducers/authenticationSlice';

// custom interceptor for 4XX errors
// fetch-intercept doesn't support async response interceptor
const fetch = window.fetch;
window.fetch = (...args) =>
  (async (args) => {
    const result = await fetch(...args);

    if (result?.status < 400 || result?.status > 499) {
      return result; // return original response
    }

    // modify response ONLY for 4XX errors
    let responseText = await result.text();

    if (responseText) {
      const jsonRes = JSON.parse(responseText);

      if (typeof jsonRes === 'object' && !jsonRes.message) {
        jsonRes.message = jsonRes.title || jsonRes.detail || ApiErrorMessage;
      }

      responseText = JSON.stringify(jsonRes);
    }

    return new Response(responseText || null, {
      status: result.status,
      statusText: result.statusText,
      headers: result.headers,
    });
  })(args);

// Handle token refresing and logging out in multiple tabs
authChannel.onmessage = (message) => {
  switch (message.key) {
    case AuthBroadcastChannelKeys.TOKEN_REFRESHING:
      switch (message.status) {
        case AUTH_MESSAGE_STATUS.IN_PROCESS:
          isRefreshing = true;
          break;

        case AUTH_MESSAGE_STATUS.SUCCESS:
          isRefreshing = false;
          onRefreshed(message.data);
          subscribers = [];
          break;

        case AUTH_MESSAGE_STATUS.FAILED:
          isRefreshing = false;
          subscribers = [];
          break;
      }
      break;
  }
};

// Fetch Interceptor
let isRefreshing = false;
let subscribers: Function[] = [];

function subscribeTokenRefresh(cb: Function) {
  subscribers.push(cb);
}

function onRefreshed(token: string) {
  subscribers.map((cb: Function) => cb(token));
}

fetchIntercept.register({
  request: async (url, config) => {
    const jwtToken = localStorage.getItem('jwt-token');
    const bullhornToken = localStorage.getItem('bullhorn-token');

    const decodedBullhornToken: any = bullhornToken
      ? JSON.parse(bullhornToken)
      : undefined;

    if (
      !jwtToken ||
      url.endsWith('/user/login') ||
      url.endsWith('/user/token/refresh') ||
      url.endsWith('/account/bullhorn/refresh-token') ||
      !url.startsWith(process.env.REACT_APP_API_BASE_URL || '') // attach JWT token only to our own APIs
    )
      return [url, config];

    if (url.endsWith('/user/logout')) {
      config.headers.Authorization = `bearer ${jwtToken}`;
      return [url, config];
    }
    const decodedToken: any = jwt_decode(jwtToken);
    const { user: userFromRedux } = store.getState().auth;

    if (userFromRedux?.userId && decodedToken.nameid !== userFromRedux.userId) {
      window.location.reload();

      // reject pending requests after page reloading
      // https://mybasepay.atlassian.net/browse/MBOM-828
      return await new Promise((resolve, reject) => {
        setTimeout(() => {
          reject();
        }, 1000);
      });
    }

    if (isTokenExpired(decodedToken.exp)) {
      const requestSubscribers: any = new Promise((resolve) => {
        subscribeTokenRefresh((token: string) => {
          config.headers.Authorization = `bearer ${token}`;
          resolve([url, config]);
        });
      });

      if (!isRefreshing) {
        isRefreshing = true;
        authChannel.postMessage({
          key: AuthBroadcastChannelKeys.TOKEN_REFRESHING,
          status: AUTH_MESSAGE_STATUS.IN_PROCESS,
        });

        let newToken: string = '';
        const result = await store.dispatch(refreshToken(jwtToken));

        if (refreshToken.fulfilled.match(result)) {
          newToken = result.payload.token || '';

          const decodedNewToken: any = jwt_decode(newToken);
          // https://mybasepay.atlassian.net/browse/MBO-2992
          // When changing the role of a teammate, you need to update the user details
          const isRoleUpdated = !isEqual(
            decodedToken.role,
            decodedNewToken.role,
          );
          isRoleUpdated && (await store.dispatch(getUserDetails()));
        } else {
          isRefreshing = false;
          authChannel.postMessage({
            key: AuthBroadcastChannelKeys.TOKEN_REFRESHING,
            status: AUTH_MESSAGE_STATUS.FAILED,
          });

          subscribers = [];
          throw new Error(result.error.message || ApiErrorMessage);
        }

        isRefreshing = false;
        authChannel.postMessage({
          key: AuthBroadcastChannelKeys.TOKEN_REFRESHING,
          status: AUTH_MESSAGE_STATUS.SUCCESS,
          data: newToken,
        });

        onRefreshed(newToken);
        subscribers = [];
      }

      return requestSubscribers;
    }

    if (userFromRedux?.bullhornEnable) {
      config.headers.BullhornToken = decodedBullhornToken?.accessToken;
    }

    config.headers.Authorization = `bearer ${jwtToken}`;
    return [url, config];
  },

  requestError: function (error) {
    // Called when an error occured during another 'request' interceptor call
    return Promise.reject(error);
  },

  response: function (response) {
    if (response?.status === 401 && !response?.url.endsWith('/user/logout')) {
      store.dispatch(logout());
    }

    return response;
  },

  responseError: function (error) {
    // Handle an fetch error
    return Promise.reject(error);
  },
});
